/**
 * Sanitizes post-login targets to prevent auth-route loops and open redirects.
 */

const BLOCKED_PATHNAMES = new Set(['/callback', '/authentikLogin', '/login']);

/**
 * Returns a safe in-app path for redirect after OIDC login.
 * Allows only same-origin relative paths; blocks auth-only routes and absolute URLs.
 *
 * @param input - Raw path from query or session (may include search string).
 * @returns A path starting with `/` suitable for `navigate` or storing in session.
 */
export function getSafePostLoginRedirect(input: string | undefined): string {
  if (!input?.trim()) {
    return '/';
  }

  const candidate = input.trim();

  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/i.test(candidate)) {
    return '/';
  }
  if (candidate.startsWith('//')) {
    return '/';
  }
  if (!candidate.startsWith('/')) {
    return '/';
  }

  const hashIndex = candidate.indexOf('#');
  const withoutHash = hashIndex >= 0 ? candidate.slice(0, hashIndex) : candidate;
  const queryIndex = withoutHash.indexOf('?');
  const pathname =
    queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const search = queryIndex >= 0 ? withoutHash.slice(queryIndex) : '';

  const normalizedPath = pathname || '/';
  if (BLOCKED_PATHNAMES.has(normalizedPath)) {
    return '/';
  }

  return `${normalizedPath}${search}`;
}

/**
 * Safe post-login target from the current window location (session-expiry flows).
 */
export function getSafePostLoginRedirectFromWindow(): string {
  const { pathname, search } = window.location;
  return getSafePostLoginRedirect(`${pathname || '/'}${search || ''}`);
}
